/**
 * voiceTalk.js — pure helpers for useVoiceSession's agent-mode fields and talk modes (CONTRACT 9.1,
 * 9.3, 10.5). No React, no browser APIs, so the node tests can import it.
 *
 * Talk modes: 'open' (every utterance counts), 'wake' (hands-free, needs the wake word) and 'ptt'
 * (push to talk). In 'ptt' the server behaves as open and the client gates the mic: audio goes out
 * only while the talk button (or the space bar) is held, followed by PTT_TRAIL_MS of silence frames
 * so the server's VAD hangover and the STT endpoint close the utterance.
 */

export const TALK_MODES = new Set(['open', 'wake', 'ptt']);
export const UI_FOCUS_MAX_BYTES = 8000;   // the voice service refuses ui_focus over 8 KB (too_large)
// Covers the VAD hangover (600 ms) and the STT endpoint delay (1500 ms) after the button is released.
export const PTT_TRAIL_MS = 1600;
const CLIENT_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;

export const isTalkMode = (v) => TALK_MODES.has(v);

/** The agent-mode fields of session.start / context.update; unknown or empty values are left out. */
export function agentFields({ mode, clientId, talkMode, uiFocus }) {
  const out = {};
  if (mode === 'agent' || mode === 'assistant') out.mode = mode;
  if (typeof clientId === 'string' && CLIENT_ID_RE.test(clientId)) out.client_id = clientId;
  if (isTalkMode(talkMode)) out.talk_mode = talkMode;
  if (uiFocus && typeof uiFocus === 'object') {
    const json = JSON.stringify(uiFocus);
    if (json.length <= UI_FOCUS_MAX_BYTES) out.ui_focus = uiFocus;
  }
  return out;
}

/** The ui_focus key used to tell whether the focus changed (updated_at alone is not a change). */
export function focusKey(uiFocus) {
  if (!uiFocus || typeof uiFocus !== 'object') return '';
  const rest = { ...uiFocus };
  delete rest.updated_at;
  return JSON.stringify(rest);
}

/**
 * The context.update for a change of mode, talk mode or focus, or null when nothing changed.
 *
 * `last` is {mode, talkMode, focus} as the server last knew it. talk_mode travels only when it
 * differs from that: a focus change must not re-send the page's talk mode, or it would overwrite a
 * switch the user made by voice ("MHI, push to talk") with the page's stale value.
 */
export function agentUpdate(last, { mode, clientId, talkMode, uiFocus }) {
  const key = focusKey(uiFocus);
  const prev = last || {};
  if (mode === prev.mode && talkMode === prev.talkMode && key === prev.focus) return null;
  const fields = agentFields({ mode, clientId, talkMode, uiFocus });
  if (talkMode === prev.talkMode) delete fields.talk_mode;
  if (Object.keys(fields).length === 0) return null;
  return { fields, last: { mode, talkMode, focus: key } };
}

/**
 * The talk mode that gates the mic, or null when no gating applies. Only agent mode gates; the
 * server's agent.state is authoritative, the page's talkMode prop is the fallback before it arrives.
 */
export function effectiveTalkMode({ mode, talkMode, agentState }) {
  if (mode !== 'agent') return null;
  if (agentState && agentState.mode === 'agent' && isTalkMode(agentState.talk_mode)) return agentState.talk_mode;
  return isTalkMode(talkMode) ? talkMode : null;
}

/**
 * What to do with one captured mic frame:
 *   'send'    — send it (and allow local barge-in)
 *   'silence' — send a zero frame of the same length (push to talk just released: close the turn)
 *   'drop'    — send nothing
 */
export function micGate({ muted = false, talkMode = null, held = false, releasedAt = 0, now = 0 }) {
  if (muted) return 'drop';
  if (talkMode !== 'ptt' || held) return 'send';
  if (releasedAt > 0 && now - releasedAt < PTT_TRAIL_MS) return 'silence';
  return 'drop';
}

/**
 * The page's talk mode after a voice agent.state: the server's value while it reports agent mode
 * (so a voice switch sticks), otherwise unchanged (assistant mode reports 'open', which must not
 * replace agent mode's 'wake' default for the next time agent mode opens).
 */
export function talkModeFromAgentState(current, agentState) {
  if (agentState && agentState.mode === 'agent' && isTalkMode(agentState.talk_mode)) return agentState.talk_mode;
  return current;
}
